import { Filter } from '../components/Modal/TaskFiltersModal';

/**
 * Generate a unique localStorage key for view-specific data
 */
export const getViewDataKey = (workspaceId: number, viewId: number, dataType: string): string => {
  return `taskonio_${dataType}_${workspaceId}_${viewId}`;
};

/**
 * Save filters to localStorage for a specific view
 */
export const saveFiltersToLocalStorage = (workspaceId: number, viewId: number, filters: Filter[]): void => {
  try {
    const key = getViewDataKey(workspaceId, viewId, 'filters');
    const selectedFilters = filters.filter((filter) => {
      const selectedOptions = filter.options?.filter((option) => option.selected) ?? [];
      return selectedOptions.length > 0;
    });
    localStorage.setItem(key, JSON.stringify(selectedFilters));
  } catch (error) {
    console.warn('Failed to save filters to localStorage:', error);
  }
};

/**
 * Load filters from localStorage for a specific view
 */
export const loadFiltersFromLocalStorage = (workspaceId: number, viewId: number): Filter[] | null => {
  try {
    const key = getViewDataKey(workspaceId, viewId, 'filters');
    const savedFilters = localStorage.getItem(key);
    if (savedFilters) {
      return JSON.parse(savedFilters) as Filter[];
    }
  } catch (error) {
    console.warn('Failed to load filters from localStorage:', error);
  }
  return null;
};

/**
 * Clear filters from localStorage for a specific view
 */
export const clearFiltersFromLocalStorage = (workspaceId: number, viewId: number): void => {
  try {
    const key = getViewDataKey(workspaceId, viewId, 'filters');
    localStorage.removeItem(key);
  } catch (error) {
    console.warn('Failed to clear filters from localStorage:', error);
  }
};

/**
 * Generic function to save any view-specific data to localStorage
 */
export const saveViewDataToLocalStorage = <T>(workspaceId: number, viewId: number, dataType: string, data: T): void => {
  try {
    const key = getViewDataKey(workspaceId, viewId, dataType);
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.warn(`Failed to save ${dataType} to localStorage:`, error);
  }
};

/**
 * Generic function to load any view-specific data from localStorage
 */
export const loadViewDataFromLocalStorage = <T>(workspaceId: number, viewId: number, dataType: string): T | null => {
  try {
    const key = getViewDataKey(workspaceId, viewId, dataType);
    const savedData = localStorage.getItem(key);
    if (savedData) {
      return JSON.parse(savedData) as T;
    }
  } catch (error) {
    console.warn(`Failed to load ${dataType} from localStorage:`, error);
  }
  return null;
};

/**
 * Clear all localStorage data for a specific view
 */
export const clearAllViewDataFromLocalStorage = (workspaceId: number, viewId: number): void => {
  try {
    const prefix = `taskonio_`;
    const viewPattern = `_${workspaceId}_${viewId}`;

    // Get all localStorage keys and remove the ones matching this view
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix) && key.endsWith(viewPattern)) {
        localStorage.removeItem(key);
      }
    }
  } catch (error) {
    console.warn('Failed to clear view data from localStorage:', error);
  }
};

/**
 * Inbox notification filter — remembered per (space, tab) on this device.
 */
const inboxFilterKey = (spaceId: number, tab: string): string => `taskonio_inbox_filter_${spaceId}_${tab}`;

export const saveInboxFilter = (spaceId: number, tab: string, filter: string): void => {
  try {
    localStorage.setItem(inboxFilterKey(spaceId, tab), filter);
  } catch (error) {
    console.warn('Failed to save inbox filter to localStorage:', error);
  }
};

export const loadInboxFilter = (spaceId: number, tab: string): string | null => {
  try {
    return localStorage.getItem(inboxFilterKey(spaceId, tab));
  } catch (error) {
    console.warn('Failed to load inbox filter from localStorage:', error);
    return null;
  }
};

export const clearInboxFilter = (spaceId: number, tab: string): void => {
  try {
    localStorage.removeItem(inboxFilterKey(spaceId, tab));
  } catch (error) {
    console.warn('Failed to clear inbox filter from localStorage:', error);
  }
};
